
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { User } from '@/models/main';

import { addDays, addMonths, lastDayOfWeek, startOfWeek, addBusinessDays } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { timeZoneDefault } from '@/utils/PortalConstants';

@Component({
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class DateRangeCustom extends Vue {
  @Prop(Boolean) private readonly disabled!: boolean;
  @Prop(Array) private readonly Pdates!: string[];
  @Prop({ type: Boolean, default: true, required: false }) private readonly showDefault!: boolean;
  @Prop({ type: Boolean, default: false, required: false }) private readonly showExtended!: boolean;
  @Prop({ type: Boolean, default: false, required: false }) private readonly showThisWeek!: boolean;
  @Prop({ type: Boolean, default: false, required: false }) private readonly showTomorrow!: boolean;
  @Prop({ type: Number, default: 6, required: false }) private readonly defaultRange!: number;
  @Prop({ type: Number, default: 12, required: false }) private readonly extendedRange!: number;
  // starting range from Today or same value in the past?
  @Prop({ type: Boolean, default: false, required: false }) private readonly fromToday!: boolean;
  // by default adding range are in months, but could be set to business days
  @Prop({ type: Boolean, default: false, required: false }) private readonly useDays!: boolean;

  private user!: User;
  private today = '';
  private menu = false;
  private dates: string[] = [];
  // datePickerKey is used as a dirty hack to force the DatePicker update after using functions to set date ranges
  // see https://github.com/nathanreyes/v-calendar/issues/389
  private datePickerKey = 0;

  private get dateRangeText(): string {
    return this.dates.slice().sort().join(' - ');
  }
  private set dateRangeText(val: string) {
    this.dates = [];
  }

  private menuClose() {
    if (this.dates.length === 2) {
      this.menu = false;
    }
  }
  private userTimeZone = '';

  @Watch('menu')
  private changeDates() {
    if (!this.menu) {
      this.$emit('changeDate', this.dates);
    }
  }

  @Watch('Pdates', { immediate: true })
  private watchToDateProps() {
    this.dates = this.Pdates;
  }

  private todayClick(): void {
    this.dates = [this.today, this.today];
    this.datePickerKey++;
  }

  private setNextBusinessDay(): void {
    const tomorrow = format(utcToZonedTime(addBusinessDays(new Date(), 1), this.userTimeZone), 'yyyy-MM-dd');
    this.dates = [tomorrow, tomorrow];
    this.datePickerKey++;
  }

  private setDefaultDates(): void {
    let fromDate = '';
    let toDate = '';
    let fromDefaultRange, toDefaultRange;
    if (this.useDays) {
      fromDefaultRange = addDays(new Date(), -this.defaultRange);
      toDefaultRange = addDays(new Date(), this.defaultRange);
    } else {
      fromDefaultRange = addMonths(new Date(), -this.defaultRange);
      toDefaultRange = addMonths(new Date(), this.defaultRange);
    }
    // by default -/+ 6 months
    if (this.fromToday) {
      fromDate = format(utcToZonedTime(new Date(), this.userTimeZone), 'yyyy-MM-dd');
    } else {
      fromDate = format(utcToZonedTime(fromDefaultRange, this.userTimeZone), 'yyyy-MM-dd');
    }
    toDate = format(utcToZonedTime(toDefaultRange, this.userTimeZone), 'yyyy-MM-dd');
    this.dates = [fromDate, toDate];
    this.datePickerKey++;
  }

  private setExtendedDates(): void {
    let fromDate = '';
    let toDate = '';
    let fromExtendedRange, toExtendedRange;
    if (this.useDays) {
      fromExtendedRange = addDays(new Date(), -this.extendedRange);
      toExtendedRange = addDays(new Date(), this.extendedRange);
    } else {
      fromExtendedRange = addMonths(new Date(), -this.extendedRange);
      toExtendedRange = addMonths(new Date(), this.extendedRange);
    }
    // by default -/+ 1 year
    if (this.fromToday) {
      fromDate = format(utcToZonedTime(new Date(), this.userTimeZone), 'yyyy-MM-dd');
    } else {
      fromDate = format(utcToZonedTime(fromExtendedRange, this.userTimeZone), 'yyyy-MM-dd');
    }
    toDate = format(utcToZonedTime(toExtendedRange, this.userTimeZone), 'yyyy-MM-dd');
    this.dates = [fromDate, toDate];
    this.datePickerKey++;
  }

  private setThisWeekDates(): void {
    let fromDate = '';
    let toDate = '';
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const endOfWeek = lastDayOfWeek(new Date(), { weekStartsOn: 1 });
    fromDate = format(utcToZonedTime(weekStart, this.userTimeZone), 'yyyy-MM-dd');
    toDate = format(utcToZonedTime(endOfWeek, this.userTimeZone), 'yyyy-MM-dd');
    this.dates = [fromDate, toDate];
    this.datePickerKey++;
  }

  private mounted(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.userTimeZone = userTimeZone;
    this.today = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd'); // today
  }
}
